import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, PlugZap, Copy, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Header } from "./common";

// endereço completo deste site (a API é relativa desde a troca de domínio)
const BACKEND = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const F = ({ label, hint, children }) => <div className="space-y-1.5"><Label>{label}</Label>{children}{hint && <p className="text-xs text-slate-400">{hint}</p>}</div>;

/** Asaas: a chave é colada aqui uma vez (não volta para a tela); ao salvar, o servidor confere a conta e cadastra sozinho o aviso automático de pagamentos. */
function AsaasCard() {
  const [a, setA] = useState(null);
  const [chave, setChave] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/asaas").then((r) => setA(r.data)).catch(() => setA({})); }, []);
  const salvar = async (corpo, ok) => {
    setBusy(true);
    try { setA((await api.put("/admin/asaas", corpo)).data); setChave(""); toast.success(ok); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!a) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 space-y-4" data-testid="asaas-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-slate-800">Asaas</div>
          <p className="text-xs text-slate-500">Cursos, combos, links de venda e cobranças. Os pacotes da IEP Intelligence continuam no Mercado Pago.</p>
        </div>
        {a.configurado && (
          <label className="flex items-center gap-2 text-sm shrink-0"><Switch checked={!!a.cursos} disabled={busy} onCheckedChange={(v) => salvar({ cursos: v }, v ? "Vendas de cursos agora pelo Asaas." : "Vendas de cursos voltaram para o Mercado Pago.")} data-testid="asaas-cursos" /> {a.cursos ? "Recebendo pelo Asaas" : "Desligado"}</label>
        )}
      </div>
      {a.configurado && (
        <div className="flex items-start gap-2 rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
          <div>Conectado à conta <b>{a.conta || "Asaas"}</b>{a.ambiente === "sandbox" ? " (ambiente de testes)" : ""}.{a.webhook ? " Aviso automático de pagamentos cadastrado." : ""}</div>
        </div>
      )}
      <F label={a.configurado ? "Trocar a chave da API" : "Chave da API (produção)"} hint="No Asaas: Integrações → Chave de API → Gerar chave. Cole aqui; ela fica guardada só no servidor e não aparece de novo nesta tela.">
        <div className="flex gap-2">
          <Input type="password" value={chave} onChange={(e) => setChave(e.target.value)} placeholder="$aact_prod_..." autoComplete="off" data-testid="asaas-chave" />
          <Button type="button" disabled={busy || chave.trim().length < 20} onClick={() => salvar({ api_key: chave.trim() }, "Asaas conectado.")} className="bg-[#232D4F] hover:bg-[#1a2240] text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4 mr-1.5" />} Conectar
          </Button>
        </div>
      </F>
    </div>
  );
}

/** Financeiro → Configurações: credenciais do Mercado Pago, parcelas e vencimento padrão. */
export default function FinanceSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);
  const [show, setShow] = useState(false);
  useEffect(() => { api.get("/settings/payments").then((r) => setS(r.data)).catch(() => setS({})); }, []);
  if (!s) return <div className="grid place-items-center h-64"><Loader2 className="h-6 w-6 animate-spin text-[#232D4F]" /></div>;
  const mp = s.mercadopago || {};
  const set = (k, v) => setS((p) => ({ ...p, mercadopago: { ...(p.mercadopago || {}), [k]: v } }));
  const webhook = `${BACKEND}/api/payments/mercadopago/webhook`;

  const save = async () => {
    setSaving(true);
    try { setS((await api.put("/settings/payments", { mercadopago: { ...mp, access_token: (mp.access_token || "").trim(), public_key: (mp.public_key || "").trim() } })).data); toast.success("Configurações salvas."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const doTest = async () => {
    setTesting(true); setTest(null);
    try { await save(); const r = await api.post("/admin/finance/test"); setTest(r.data); toast.success(`Conectado à conta ${r.data.account}.`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setTesting(false); }
  };

  return (
    <div className="space-y-5 animate-fade-up max-w-3xl">
      <Header title="Configurações financeiras" sub="Contas que recebem as vendas e cobranças (Asaas e Mercado Pago)." />
      <AsaasCard />
      <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-slate-800">Mercado Pago</div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!mp.enabled} onCheckedChange={(v) => set("enabled", v)} /> {mp.enabled ? "Ativo" : "Inativo"}</label>
        </div>
        <F label="Public Key" hint="Mercado Pago → Seu negócio → Configurações → Credenciais de produção."><Input value={mp.public_key || ""} onChange={(e) => set("public_key", e.target.value)} placeholder="APP_USR-..." /></F>
        <F label="Access Token">
          <div className="flex gap-2">
            <Input type={show ? "text" : "password"} value={mp.access_token || ""} onChange={(e) => set("access_token", e.target.value)} placeholder="APP_USR-..." data-testid="mp-token" />
            <Button type="button" variant="outline" onClick={() => setShow((v) => !v)}>{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
          </div>
        </F>
        <F label="Assinatura secreta do webhook (recomendado)" hint="Em Suas integrações → Webhooks, cadastre a URL abaixo (evento “Pagamentos”) e cole aqui a assinatura secreta.">
          <Input type="password" value={mp.webhook_secret || ""} onChange={(e) => set("webhook_secret", e.target.value)} />
        </F>
        <F label="URL do webhook (para cadastrar no Mercado Pago)">
          <div className="flex gap-2"><Input readOnly value={webhook} className="font-mono text-xs" />
            <Button type="button" variant="outline" onClick={() => { navigator.clipboard.writeText(webhook); toast.success("URL copiada."); }}><Copy className="h-4 w-4" /></Button></div>
        </F>
        <div className="grid sm:grid-cols-2 gap-4">
          <F label="Parcelas máximas no cartão">
            <select value={mp.max_installments || 12} onChange={(e) => set("max_installments", parseInt(e.target.value, 10))} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
              {[1, 2, 3, 4, 5, 6, 10, 12].map((n) => <option key={n} value={n}>{n === 1 ? "À vista" : `Até ${n}x`}</option>)}
            </select>
          </F>
          <F label="Boleto">
            <label className="h-10 flex items-center gap-2 text-sm"><Switch checked={!!mp.allow_boleto} onCheckedChange={(v) => set("allow_boleto", v)} data-testid="allow-boleto" />
              {mp.allow_boleto ? "Aceitar boleto (e lotérica)" : "Desligado: só Pix e cartão"}</label>
          </F>
          <F label="Vencimento padrão das cobranças">
            <select value={mp.charge_due_days || 3} onChange={(e) => set("charge_due_days", parseInt(e.target.value, 10))} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
              {[1, 2, 3, 5, 7, 10, 15, 30].map((n) => <option key={n} value={n}>{n} dia(s)</option>)}
            </select>
          </F>
        </div>
        {test && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800 flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 mt-0.5" />
            <div>Conectado à conta <strong>{test.account}</strong>{test.test_mode ? " (credencial de TESTE — pagamentos não são reais)" : ""}.</div>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={doTest} disabled={testing || !mp.access_token}>{testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PlugZap className="h-4 w-4 mr-1.5" /> Testar conexão</>}</Button>
          <Button onClick={save} disabled={saving} className="text-white" style={{ background: "#1E9E2A" }}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Salvar</>}</Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 space-y-4">
        <div className="font-semibold text-slate-800">Divisão de vendas (Recebedores) — aplicativo Mercado Pago</div>
        <p className="text-sm text-slate-500">Para dividir automaticamente o valor com professores/parceiros, crie um aplicativo em <em>mercadopago.com.br/developers → Suas integrações → Criar aplicação</em> e cole aqui o <strong>Client ID</strong> e o <strong>Client Secret</strong>. A ativação da divisão é a próxima etapa.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <F label="Client ID"><Input value={mp.client_id || ""} onChange={(e) => set("client_id", e.target.value)} /></F>
          <F label="Client Secret"><Input type="password" value={mp.client_secret || ""} onChange={(e) => set("client_secret", e.target.value)} /></F>
        </div>
        <F label="URL de redirecionamento (cadastrar no aplicativo)">
          <div className="flex gap-2"><Input readOnly value={`${BACKEND}/api/payments/mercadopago/oauth`} className="font-mono text-xs" />
            <Button type="button" variant="outline" onClick={() => { navigator.clipboard.writeText(`${BACKEND}/api/payments/mercadopago/oauth`); toast.success("URL copiada."); }}><Copy className="h-4 w-4" /></Button></div>
        </F>
        <div className="flex justify-end"><Button onClick={save} disabled={saving} variant="outline"><Save className="h-4 w-4 mr-1.5" /> Salvar</Button></div>
      </div>
    </div>
  );
}
